import { jsx as _jsx } from "react/jsx-runtime";
import React, { useRef, useEffect, useCallback } from 'react';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import { EditorState, Prec } from '@codemirror/state';
import { sql, SQLite } from '@codemirror/lang-sql';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { oneDark } from '@codemirror/theme-one-dark';
import { autocompletion, completionStatus } from '@codemirror/autocomplete';
import { parseSchemaMap } from '../../data/schemaParser';
export function SqlEditor({ onExecute, getValueRef, initialValue = '' }) {
    const containerRef = useRef(null);
    const viewRef = useRef(null);
    const onExecuteRef = useRef(onExecute);
    onExecuteRef.current = onExecute;
    const handleExecute = useCallback(() => {
        if (!viewRef.current)
            return;
        const value = viewRef.current.state.doc.toString().trim();
        if (value) {
            onExecuteRef.current(value);
        }
    }, []);
    useEffect(() => {
        if (!containerRef.current)
            return;
        const state = EditorState.create({
            doc: initialValue,
            extensions: [
                keymap.of([
                    {
                        key: 'Ctrl-Enter',
                        mac: 'Cmd-Enter',
                        run: () => {
                            handleExecute();
                            return true;
                        },
                    },
                    ...defaultKeymap,
                    ...historyKeymap,
                ]),
                history(),
                sql({ dialect: SQLite, schema: parseSchemaMap() }),
                autocompletion(),
                // Escape clears editor only when autocomplete is not active
                Prec.low(keymap.of([{
                        key: 'Escape',
                        run: (view) => {
                            if (completionStatus(view.state) !== null)
                                return false;
                            view.dispatch({
                                changes: { from: 0, to: view.state.doc.length, insert: '' },
                            });
                            return true;
                        },
                    }])),
                oneDark,
                placeholder('Напиши SQL-запрос...'),
                EditorView.lineWrapping,
                EditorView.theme({
                    '&': {
                        fontSize: '14px',
                        minHeight: '100px',
                    },
                    '.cm-content': {
                        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                        padding: '12px',
                    },
                    '.cm-gutters': {
                        background: '#16213e',
                        borderRight: '1px solid #2a2a4a',
                    },
                    '.cm-activeLine': {
                        background: 'rgba(78, 204, 163, 0.05)',
                    },
                    '.cm-selectionBackground': {
                        background: 'rgba(78, 204, 163, 0.2) !important',
                    },
                }),
            ],
        });
        const view = new EditorView({
            state,
            parent: containerRef.current,
        });
        viewRef.current = view;
        if (getValueRef) {
            getValueRef.current = () => view.state.doc.toString().trim();
        }
        return () => {
            view.destroy();
            viewRef.current = null;
            if (getValueRef) {
                getValueRef.current = null;
            }
        };
    }, [handleExecute, initialValue, getValueRef]);
    return _jsx("div", { ref: containerRef });
}
